/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `chrono_shot_session` message.
 *
 * Specifically used for XERO products.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class ChronoShotSessionMesg : Mesg {
    public constructor() : super("ChronoShotSession", Profile.MesgNum.CHRONO_SHOT_SESSION)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** min_speed, in m/s. */
    public var minSpeed: Double?
        get() = getFieldValue(MIN_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MIN_SPEED_FIELD_NUM, value)

    /** max_speed, in m/s. */
    public var maxSpeed: Double?
        get() = getFieldValue(MAX_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_SPEED_FIELD_NUM, value)

    /** avg_speed, in m/s. */
    public var avgSpeed: Double?
        get() = getFieldValue(AVG_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_SPEED_FIELD_NUM, value)

    /** shot_count. */
    public var shotCount: UShort?
        get() = getFieldValue(SHOT_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SHOT_COUNT_FIELD_NUM, value)

    /** projectile_type. */
    public var projectileType: ProjectileType?
        get() = (getFieldValue(PROJECTILE_TYPE_FIELD_NUM) as? UByte)?.let { ProjectileType.fromValue(it) }
        set(value) = setFieldValue(PROJECTILE_TYPE_FIELD_NUM, value?.value)

    /** grain_weight, in gr. */
    public var grainWeight: Double?
        get() = getFieldValue(GRAIN_WEIGHT_FIELD_NUM) as? Double
        set(value) = setFieldValue(GRAIN_WEIGHT_FIELD_NUM, value)

    /** standard_deviation, in m/s. */
    public var standardDeviation: Double?
        get() = getFieldValue(STANDARD_DEVIATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(STANDARD_DEVIATION_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val MIN_SPEED_FIELD_NUM: UByte = 0u
        public const val MAX_SPEED_FIELD_NUM: UByte = 1u
        public const val AVG_SPEED_FIELD_NUM: UByte = 2u
        public const val SHOT_COUNT_FIELD_NUM: UByte = 3u
        public const val PROJECTILE_TYPE_FIELD_NUM: UByte = 4u
        public const val GRAIN_WEIGHT_FIELD_NUM: UByte = 5u
        public const val STANDARD_DEVIATION_FIELD_NUM: UByte = 6u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("MinSpeed", 0u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[1] = Field("MaxSpeed", 1u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[2] = Field("AvgSpeed", 2u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[3] = Field("ShotCount", 3u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[4] = Field("ProjectileType", 4u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[5] = Field("GrainWeight", 5u, BaseType.UINT32, 10.0, 0.0, "gr", false)
            fields[6] = Field("StandardDeviation", 6u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            return fields
        }
    }
}
